import { ref } from 'vue'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/store/authStore'
import { useSchoolStore } from '@/store/schoolStore'
import { NO_SCHOOL_FOR_SETTINGS } from '@/utils/schoolSettings'

// Default theme configuration
export const DEFAULT_THEME = {
  // Primary Colors
  primaryColor: '#1976d2',
  primaryHover: '#1565c0',
  primaryLight: '#e3f2fd',
  primaryDark: '#0d47a1',

  // Secondary Colors
  secondaryColor: '#6c757d',
  secondaryHover: '#545b62',
  secondaryLight: '#f8f9fa',
  secondaryDark: '#495057',

  // Background Colors
  bgPrimary: '#e8ecf1',
  bgSecondary: '#ffffff',
  bgTertiary: '#f0f2f5',
  bgMuted: '#dde1e6',

  // Text Colors
  textPrimary: '#333333',
  textSecondary: '#666666',
  textMuted: '#999999',
  textInverse: '#ffffff',
  headerTextColor: '#1a1a1a',
  // Readable pair for student names / other body-text brand uses.
  // Buttons keep primaryColor; names use this darker (or lighter, on dark) ink.
  nameColor: '#0d47a1',

  // Accent Colors
  accentColor: '#e3f2fd',

  // Border Colors
  borderColor: '#dee2e6',
  borderLight: '#e9ecef',
  borderDark: '#adb5bd',
  borderFocus: '#1976d2',

  // Status Colors
  successColor: '#28a745',
  successHover: '#218838',
  successLight: '#d4edda',
  warningColor: '#ffc107',
  warningHover: '#e0a800',
  warningLight: '#fff3cd',
  errorColor: '#dc3545',
  errorHover: '#c82333',
  errorLight: '#f8d7da',
  infoColor: '#17a2b8',
  infoHover: '#138496',
  infoLight: '#d1ecf1',

  // Admin Panel Colors
  adminTabBg: '#f8f9fa',
  adminTabActive: '#2a79c9',
  adminTabHover: '#e3eaf6',

  // Table Colors
  tableHeaderBg: '#dfe7f3',
  tableStripeBg: '#f8fafd',
  tableBorder: '#d1d1d1',
  tableHover: '#f0f0f0',

  // Border Radius
  borderRadiusSm: '4px',
  borderRadiusMd: '6px',
  borderRadiusLg: '8px',
  borderRadiusXl: '12px',

  // Shadows
  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  shadowMd: '0 2px 4px rgba(0, 0, 0, 0.1)',
  shadowLg: '0 4px 6px rgba(0, 0, 0, 0.1)',
  shadowXl: '0 4px 20px rgba(0, 0, 0, 0.3)',

  // Font Family
  fontFamilyBase: "'Inter', 'Segoe UI', 'Arial', sans-serif",

  // Font Sizes
  fontSizeXs: '0.75rem',
  fontSizeSm: '0.875rem',
  fontSizeBase: '1rem',
  fontSizeLg: '1.125rem',
  fontSizeXl: '1.25rem',
  fontSizeXxl: '1.5rem',

  // Style Options
  borderStyle: 'rounded',
  shadowStyle: 'subtle',
  contrastLevel: 'standard',
  lineHeight: '1.5',

  // Feature Toggles
  classServiceBorderWidth: '2px'
}

const THEME_FIELD_KEYS = Object.keys(DEFAULT_THEME)

export const THEME_WRITE_DENIED =
  'Theme can only be saved by a school or district admin for the active school.'

/** Roles that may persist schools/{id}.theme (mirrors administersSchool, minus coordinator). */
export function canWriteSchoolTheme(role, { schoolId = null, schoolIds = null } = {}) {
  if (!schoolId) return false
  if (role === 'district_admin') return true
  if (role !== 'admin' && role !== 'school_admin') return false
  return Array.isArray(schoolIds) && schoolIds.includes(schoolId)
}

export function pickThemeFields(raw = {}) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const picked = {}
  for (const key of THEME_FIELD_KEYS) {
    if (raw[key] !== undefined) picked[key] = raw[key]
  }
  return picked
}

/** District fallback, then this school's overlay. Legacy app_settings/theme is the fallback. */
export function resolveTheme({ district = null, school = null } = {}) {
  const districtFields = pickThemeFields(district)
  const schoolFields = pickThemeFields(school)
  const resolved = {
    ...DEFAULT_THEME,
    ...districtFields,
    ...schoolFields
  }
  // Older school saves only stored primaryColor. Derive a readable name pair
  // so orange/gold buttons do not also paint student names.
  if (schoolFields.nameColor == null && districtFields.nameColor == null) {
    resolved.nameColor =
      resolved.contrastLevel === 'maximum'
        ? resolved.textPrimary
        : deriveNameColor(resolved.primaryColor, resolved.bgSecondary)
  }
  // High Contrast used to set fontSizeBase to 1.125rem without scaling sm/lg.
  // Body/inputs jumped; district labels stayed at 0.85–0.92rem. Undo that
  // leftover unless the Font Size control already rebuilt the scale.
  if (
    resolved.contrastLevel === 'maximum' &&
    resolved.fontSizeBase === '1.125rem' &&
    resolved.fontSizeSm === DEFAULT_THEME.fontSizeSm
  ) {
    resolved.fontSizeBase = DEFAULT_THEME.fontSizeBase
  }
  return resolved
}

/** Professional page chrome with High Contrast ink and stronger edges.
 *  Does not bump font size — that stays on the Font Size control. */
export const CLEAR_LOOK = {
  primaryColor: '#1565c0',
  primaryHover: '#0d47a1',
  primaryLight: '#e3f2fd',
  primaryDark: '#0d47a1',
  bgPrimary: '#e8ecf1',
  bgSecondary: '#ffffff',
  bgTertiary: '#eef1f5',
  bgMuted: '#d5dbe3',
  textPrimary: '#121212',
  textSecondary: '#2c3540',
  textMuted: '#3d4a56',
  textInverse: '#ffffff',
  headerTextColor: '#0a1628',
  nameColor: '#0a2540',
  accentColor: '#e3f2fd',
  secondaryColor: '#4a5560',
  secondaryHover: '#2c3540',
  borderColor: '#7b8694',
  borderLight: '#c5ccd4',
  borderDark: '#4a5560',
  borderFocus: '#1565c0',
  adminTabBg: '#f4f6f8',
  adminTabActive: '#1565c0',
  adminTabHover: '#e4ebf3',
  tableHeaderBg: '#d4deea',
  tableStripeBg: '#f4f7fa',
  tableBorder: '#9aa3ad',
  tableHover: '#e8eef4',
  borderStyle: 'rounded',
  shadowStyle: 'subtle',
  contrastLevel: 'high',
  borderRadiusSm: '3px',
  borderRadiusMd: '5px',
  borderRadiusLg: '7px',
  borderRadiusXl: '10px',
  lineHeight: '1.55'
}

export const DISTRICT_DEFAULT_PRESET_ID = 'clear'

/** Which Look card the Appearance editor should highlight for a saved theme. */
export function inferLookName(theme = {}) {
  if (theme.contrastLevel === 'maximum' || theme.borderStyle === 'sharp') return 'High Contrast'
  const raw = String(theme.bgPrimary || '').replace('#', '')
  if (raw.length >= 6) {
    const r = parseInt(raw.substring(0, 2), 16)
    const g = parseInt(raw.substring(2, 4), 16)
    const b = parseInt(raw.substring(4, 6), 16)
    if ((0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5) return 'Dark Mode'
  }
  if (theme.contrastLevel === 'high') return 'Clear'
  return 'Professional'
}

/** District Dashboard is always Clear. School pages keep campus branding. */
export function resolveDistrictTheme() {
  return {
    presetId: DISTRICT_DEFAULT_PRESET_ID,
    theme: { ...DEFAULT_THEME, ...pickThemeFields(CLEAR_LOOK) }
  }
}

// CSS Variable mapping
const CSS_VARIABLE_MAP = {
  primaryColor: '--primary-color',
  primaryHover: '--primary-hover',
  primaryLight: '--primary-light',
  primaryDark: '--primary-dark',
  secondaryColor: '--secondary-color',
  secondaryHover: '--secondary-hover',
  secondaryLight: '--secondary-light',
  secondaryDark: '--secondary-dark',
  bgPrimary: '--bg-primary',
  bgSecondary: '--bg-secondary',
  bgTertiary: '--bg-tertiary',
  bgMuted: '--bg-muted',
  textPrimary: '--text-primary',
  textSecondary: '--text-secondary',
  textMuted: '--text-muted',
  textInverse: '--text-inverse',
  headerTextColor: '--header-text-color',
  nameColor: '--name-color',
  accentColor: '--accent-color',
  borderColor: '--border-color',
  borderLight: '--border-light',
  borderDark: '--border-dark',
  borderExtraDark: '--border-extra-dark',
  borderFocus: '--border-focus',
  successColor: '--success-color',
  successHover: '--success-hover',
  successLight: '--success-light',
  warningColor: '--warning-color',
  warningHover: '--warning-hover',
  warningLight: '--warning-light',
  errorColor: '--error-color',
  errorHover: '--error-hover',
  errorLight: '--error-light',
  infoColor: '--info-color',
  infoHover: '--info-hover',
  infoLight: '--info-light',
  adminTabBg: '--admin-tab-bg',
  adminTabActive: '--admin-tab-active',
  adminTabHover: '--admin-tab-hover',
  tableHeaderBg: '--table-header-bg',
  tableStripeBg: '--table-stripe-bg',
  tableBorder: '--table-border',
  tableHover: '--table-hover',
  borderRadiusSm: '--border-radius-sm',
  borderRadiusMd: '--border-radius-md',
  borderRadiusLg: '--border-radius-lg',
  borderRadiusXl: '--border-radius-xl',
  shadowSm: '--shadow-sm',
  shadowMd: '--shadow-md',
  shadowLg: '--shadow-lg',
  shadowXl: '--shadow-xl',
  fontFamilyBase: '--font-family-base',
  fontSizeXs: '--font-size-xs',
  fontSizeSm: '--font-size-sm',
  fontSizeBase: '--font-size-base',
  fontSizeLg: '--font-size-lg',
  fontSizeXl: '--font-size-xl',
  fontSizeXxl: '--font-size-xxl',
  lineHeight: '--line-height-base',
  classServiceBorderWidth: '--class-service-border-width'
}

// Color utility: hex -> { h, s, l }
function hexToHsl(hex) {
  hex = hex.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  let h,
    s,
    l = (max + min) / 2
  if (max === min) {
    h = s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) }
}

// Color utility: { h, s, l } -> hex
function hslToHex(h, s, l) {
  s /= 100
  l /= 100
  const a = s * Math.min(l, 1 - l)
  const f = (n) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * Math.max(0, Math.min(1, color)))
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

export function normalizeHex(hex) {
  if (typeof hex !== 'string') return null
  let v = hex.trim()
  if (v.startsWith('#')) v = v.slice(1)
  if (v.length === 3) {
    v = `${v[0]}${v[0]}${v[1]}${v[1]}${v[2]}${v[2]}`
  }
  if (!/^[0-9a-fA-F]{6}$/.test(v)) return null
  return `#${v.toLowerCase()}`
}

function srgbChannelToLinear(c) {
  const v = c / 255
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
}

export function relativeLuminance(hex) {
  const n = normalizeHex(hex)
  if (!n) return 0
  const r = parseInt(n.slice(1, 3), 16)
  const g = parseInt(n.slice(3, 5), 16)
  const b = parseInt(n.slice(5, 7), 16)
  return (
    0.2126 * srgbChannelToLinear(r) +
    0.7152 * srgbChannelToLinear(g) +
    0.0722 * srgbChannelToLinear(b)
  )
}

export function contrastRatio(a, b) {
  const l1 = relativeLuminance(a)
  const l2 = relativeLuminance(b)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

function isWarmLoudHue(h, s) {
  // Orange / amber / gold stay "loud" at mid lightness — poor as name text.
  return s >= 45 && h >= 12 && h <= 72
}

const NAME_CONTRAST_TARGET = 7
const NAME_CONTRAST_FLOOR = 4.5

/**
 * Pair a school (button) color with a readable name/ink color.
 * Same hue, darker on light surfaces or lighter on dark, aiming for ~7:1.
 */
export function deriveNameColor(schoolHex, backgroundHex = '#ffffff') {
  const hex = normalizeHex(schoolHex) || '#1976d2'
  const bg = normalizeHex(backgroundHex) || '#ffffff'
  const lightBg = relativeLuminance(bg) >= 0.45
  const { h, s } = hexToHsl(hex)
  if (s < 8) return lightBg ? '#1a1a1a' : '#f2f2f2'

  const textS = Math.min(Math.max(s, 40), 78)
  let l = lightBg ? (isWarmLoudHue(h, s) ? 28 : 32) : isWarmLoudHue(h, s) ? 80 : 76

  let best = hslToHex(h, textS, l)
  const step = lightBg ? -2 : 2
  let guard = 0
  while (guard++ < 40 && contrastRatio(best, bg) < NAME_CONTRAST_TARGET) {
    const next = l + step
    if (lightBg ? next < 12 : next > 90) break
    l = next
    best = hslToHex(h, textS, l)
  }
  if (contrastRatio(best, bg) < NAME_CONTRAST_FLOOR) {
    return lightBg ? '#1a1a1a' : '#f2f2f2'
  }
  return best
}

function generateColorVariations(hex, isDarkTheme = false, backgroundHex) {
  const { h } = hexToHsl(hex)
  const { s, l } = hexToHsl(hex)
  const nameBg = normalizeHex(backgroundHex) || (isDarkTheme ? '#1e1e1e' : '#ffffff')
  const nameColor = deriveNameColor(hex, nameBg)
  if (isDarkTheme) {
    return {
      primaryColor: hex,
      primaryHover: hslToHex(h, Math.min(s + 5, 100), Math.min(l + 12, 95)),
      primaryDark: hslToHex(h, s, Math.max(l - 10, 10)),
      primaryLight: hslToHex(h, Math.max(s - 30, 15), 15),
      accentColor: hslToHex(h, Math.max(s - 30, 15), 15),
      borderFocus: hex,
      adminTabActive: hex,
      bgPrimary: hslToHex(h, 10, 8),
      bgTertiary: hslToHex(h, 8, 17),
      bgMuted: hslToHex(h, 8, 22),
      tableHeaderBg: hslToHex(h, 12, 11),
      tableStripeBg: hslToHex(h, 8, 10),
      nameColor
    }
  }
  return {
    primaryColor: hex,
    primaryHover: hslToHex(h, s, Math.max(l - 10, 5)),
    primaryDark: hslToHex(h, s, Math.max(l - 20, 5)),
    primaryLight: hslToHex(h, Math.max(s - 20, 10), Math.min(l + 35, 95)),
    accentColor: hslToHex(h, Math.max(s - 20, 10), Math.min(l + 35, 95)),
    borderFocus: hex,
    adminTabActive: hex,
    bgPrimary: hslToHex(h, 12, 89),
    bgTertiary: hslToHex(h, 10, 93),
    bgMuted: hslToHex(h, 10, 85),
    tableHeaderBg: hslToHex(h, 15, 86),
    tableStripeBg: hslToHex(h, 8, 96),
    nameColor
  }
}

const currentTheme = ref({ ...DEFAULT_THEME })
const loading = ref(false)
const error = ref(null)
const isInitialized = ref(false)
let loadGeneration = 0

async function readDistrictTheme() {
  try {
    const snap = await getDoc(doc(db, 'app_settings', 'theme'))
    return snap.exists() ? snap.data() : null
  } catch {
    return null
  }
}

async function readSchoolTheme(schoolId) {
  if (!schoolId) return null
  try {
    const snap = await getDoc(doc(db, 'schools', schoolId))
    const theme = snap.exists() ? snap.data()?.theme : null
    return theme && typeof theme === 'object' ? theme : null
  } catch {
    return null
  }
}

function activeSchoolId() {
  try {
    return useSchoolStore().currentSchoolId || null
  } catch {
    return null
  }
}

export function useThemeManager() {
  const loadTheme = async () => {
    const gen = ++loadGeneration
    loading.value = true
    error.value = null

    try {
      const schoolId = activeSchoolId()
      const district = await readDistrictTheme()
      if (gen !== loadGeneration) return currentTheme.value
      const school = await readSchoolTheme(schoolId)
      if (gen !== loadGeneration) return currentTheme.value

      currentTheme.value = resolveTheme({ district, school })
      isInitialized.value = true
      applyTheme(currentTheme.value)
      return currentTheme.value
    } catch (err) {
      error.value = err.message
      console.error('Error loading theme:', err)
      throw err
    } finally {
      if (gen === loadGeneration) loading.value = false
    }
  }

  const saveTheme = async (theme = currentTheme.value) => {
    loading.value = true
    error.value = null

    try {
      const schoolId = activeSchoolId()
      if (!schoolId) throw new Error(NO_SCHOOL_FOR_SETTINGS)
      const user = useAuthStore().currentUser
      if (
        !canWriteSchoolTheme(user?.role, {
          schoolId,
          schoolIds: user?.schoolIds
        })
      ) {
        throw new Error(THEME_WRITE_DENIED)
      }

      const fields = pickThemeFields(theme)
      await setDoc(doc(db, 'schools', schoolId), { theme: fields }, { merge: true })

      currentTheme.value = { ...DEFAULT_THEME, ...fields }
      applyTheme(currentTheme.value)
      return true
    } catch (err) {
      error.value = err.message
      console.error('Error saving theme:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadDistrictDashboardTheme = async () => {
    const gen = ++loadGeneration
    loading.value = true
    error.value = null
    try {
      const { theme } = resolveDistrictTheme()
      if (gen !== loadGeneration) return currentTheme.value
      currentTheme.value = theme
      isInitialized.value = true
      applyTheme(theme)
      return theme
    } catch (err) {
      error.value = err.message
      console.error('Error loading district theme:', err)
      throw err
    } finally {
      if (gen === loadGeneration) loading.value = false
    }
  }

  // Apply theme to CSS variables
  const applyTheme = (theme) => {
    const root = document.documentElement

    Object.entries(CSS_VARIABLE_MAP).forEach(([themeKey, cssVar]) => {
      if (theme[themeKey] !== undefined) {
        root.style.setProperty(cssVar, theme[themeKey])
      }
    })

    if (!theme.nameColor && theme.primaryColor) {
      const ink =
        theme.contrastLevel === 'maximum'
          ? theme.textPrimary || '#000000'
          : deriveNameColor(theme.primaryColor, theme.bgSecondary || '#ffffff')
      root.style.setProperty('--name-color', ink)
    }

    // Class-service highlights are an on/off feature: normalize any enabled
    // width (legacy saves may hold 3px/5px) to the standard 2px, and drive the
    // background-tint visibility from the same setting.
    const highlightsOff = theme.classServiceBorderWidth === '0px'
    root.style.setProperty('--class-service-border-width', highlightsOff ? '0px' : '2px')
    root.style.setProperty('--class-service-flag-opacity', highlightsOff ? '0' : '1')
  }

  // Update a single theme property
  const updateThemeProperty = (property, value) => {
    currentTheme.value[property] = value
    applyTheme(currentTheme.value)
  }

  // Reset theme to defaults
  const resetTheme = async () => {
    currentTheme.value = { ...DEFAULT_THEME }
    applyTheme(currentTheme.value)
    await saveTheme(currentTheme.value)
  }

  // Preview theme without saving
  const previewTheme = (theme) => {
    applyTheme(theme)
  }

  // Revert to saved theme
  const revertTheme = () => {
    applyTheme(currentTheme.value)
  }

  // Get current CSS variable value
  const getCssVariableValue = (variableName) => {
    return getComputedStyle(document.documentElement).getPropertyValue(variableName)
  }

  // Validate color value
  const validateColor = (color) => {
    const colorRegex =
      /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$|^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$|^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/
    return colorRegex.test(color)
  }

  return {
    currentTheme,
    loading,
    error,
    isInitialized,
    loadTheme,
    saveTheme,
    loadDistrictDashboardTheme,
    applyTheme,
    updateThemeProperty,
    resetTheme,
    previewTheme,
    revertTheme,
    getCssVariableValue,
    validateColor,
    generateColorVariations,
    CSS_VARIABLE_MAP,
    DEFAULT_THEME,
    canWriteSchoolTheme
  }
}
